import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListTodo, Plus, CheckCircle2, RotateCcw, Trash2, ArrowRight, TrendingUp, Loader2, X,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PlanV2Sections from "@/components/improvement/PlanV2Sections";

const CATEGORY_CLS = {
  MISSING_DATA: "bg-red-100 text-red-800",
  VERIFY_DATA: "bg-amber-100 text-amber-800",
  ADD_EVIDENCE: "bg-sky-100 text-sky-800",
};
const PILLAR_CLS = {
  E: "bg-green-100 text-green-800",
  S: "bg-sky-100 text-sky-800",
  G: "bg-indigo-100 text-indigo-800",
};

export default function ImprovementPlan() {
  const { companyId, period, can } = useTenant();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [pillar, setPillar] = useState("NONE");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const canWrite = can("esg:write");
  const [scoreRun, setScoreRun] = useState(undefined);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/improvement-plan`);
      setPlan(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
    // Macro-Area A: lo "Score attuale" mostrato in testata viene SOLO da ScoreRun.
    api.get(`/companies/${companyId}/periods/${period.id}/score-runs/current`)
      .then((r) => setScoreRun(r.data))
      .catch(() => setScoreRun(null));
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleAuto = async (action) => {
    setBusyKey(action.auto_key);
    try {
      const next = action.status === "DONE" ? "OPEN" : "DONE";
      await api.post(`/companies/${companyId}/periods/${period.id}/improvement-plan/auto-status`,
        { auto_key: action.auto_key, status: next });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/improvement-plan/actions`,
        { title: title.trim(), pillar: pillar === "NONE" ? null : pillar, description: description.trim() || null });
      toast.success("Azione aggiunta al piano");
      setTitle("");
      setDescription("");
      setPillar("NONE");
      setShowForm(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const toggleManual = async (action) => {
    setBusyKey(action.id);
    try {
      await api.patch(`/companies/${companyId}/periods/${period.id}/improvement-plan/actions/${action.id}`,
        { status: action.status === "DONE" ? "OPEN" : "DONE" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const deleteManual = async (action) => {
    if (!window.confirm(`Eliminare l'azione "${action.title}"?`)) return;
    setBusyKey(action.id);
    try {
      await api.delete(`/companies/${companyId}/periods/${period.id}/improvement-plan/actions/${action.id}`);
      toast.success("Azione eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const currentRun = scoreRun?.run;
  const insufficient = currentRun?.display_status === "INSUFFICIENT_DATA";
  const overall = insufficient ? null : currentRun?.overall_score;

  return (
    <div className="space-y-6" data-testid="improvement-plan-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Piano di miglioramento</h1>
          <p className="text-sm text-slate-600 mt-1">
            Azioni prioritarie per far crescere l'ESG PMI Score del periodo <strong>Esercizio {period?.year}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {overall !== null && overall !== undefined && (
            <span className="text-sm text-slate-700" data-testid="improvement-current-score">
              Score attuale: <strong className="text-green-800">{overall}/100</strong>
              {currentRun?.display_status === "STALE" && (
                <span className="ml-2 text-xs font-semibold text-amber-800 bg-amber-100 rounded px-1.5 py-0.5"
                  data-testid="improvement-score-stale">da ricalcolare</span>
              )}
            </span>
          )}
          {insufficient && (
            <span className="text-sm text-slate-700" data-testid="improvement-current-score">
              Score attuale: <strong className="text-slate-500">—</strong>
              <span className="ml-2 text-xs font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5"
                data-testid="improvement-score-insufficient">dati insufficienti</span>
            </span>
          )}
          <Link to="/simulazioni"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-800 bg-white border border-green-300 hover:bg-green-100 rounded-md px-4 py-2 transition-colors"
            data-testid="goto-simulator">
            Apri il Simulatore <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50" data-testid="improvement-plan-error">
          <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      {plan && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="improvement-summary">
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-slate-600">Azioni aperte</span>
            <span className="text-2xl font-extrabold text-slate-900" data-testid="improvement-open-count">{plan.summary.open}</span>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-slate-600">Completate</span>
            <span className="text-2xl font-extrabold text-green-800" data-testid="improvement-done-count">{plan.summary.done}</span>
          </CardContent></Card>
          <Card className="border-green-200 bg-green-50"><CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-green-900 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" aria-hidden="true" />Impatto potenziale</span>
            <span className="text-2xl font-extrabold text-green-800" data-testid="improvement-potential">+{plan.summary.potential_points} pt</span>
          </CardContent></Card>
        </div>
      )}

      {/* Fase 10: Opportunità (Priority Engine) + Piani versionati */}
      <PlanV2Sections companyId={companyId} period={period} canWrite={canWrite}
        canApprove={can("plan:approve")} />

      <Card className="border-slate-200" data-testid="auto-actions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <ListTodo className="w-4 h-4" aria-hidden="true" />
            Azioni suggerite dai tuoi dati
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!plan ? (
            <p className="text-sm text-slate-500">Caricamento…</p>
          ) : plan.auto_actions.length === 0 ? (
            <p className="text-sm text-slate-600 py-4" data-testid="auto-actions-empty">
              Nessun gap rilevato: tutti i dati applicabili sono presenti e verificati. Ottimo lavoro!
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="auto-actions-list">
              {plan.auto_actions.map((a, i) => {
                const done = a.status === "DONE";
                return (
                  <li key={a.auto_key} className={`py-3 flex flex-wrap items-center gap-3 ${done ? "opacity-60" : ""}`}
                    data-testid={`auto-action-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={CATEGORY_CLS[a.category]}>{a.category_label}</Badge>
                        <Badge className={PILLAR_CLS[a.pillar]}>{a.pillar_label}</Badge>
                        <span className="text-xs font-bold text-green-800" data-testid={`auto-action-impact-${i}`}>+{a.impact_points} pt</span>
                      </div>
                      <p className={`text-sm font-medium mt-1 ${done ? "line-through text-slate-500" : "text-slate-900"}`}
                        data-testid={`auto-action-title-${i}`}>{a.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{a.detail}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={a.link}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline"
                        data-testid={`auto-action-link-${i}`}>
                        Apri <ArrowRight className="w-3 h-3" aria-hidden="true" />
                      </Link>
                      {canWrite && (
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          disabled={busyKey === a.auto_key}
                          onClick={() => toggleAuto(a)}
                          data-testid={`auto-action-toggle-${i}`}>
                          {done
                            ? <><RotateCcw className="w-3 h-3 mr-1" aria-hidden="true" />Riapri</>
                            : <><CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Segna completata</>}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="manual-actions-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">Azioni manuali</CardTitle>
            {canWrite && (
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => setShowForm((v) => !v)} data-testid="toggle-manual-form">
                {showForm ? <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                {showForm ? "Annulla" : "Nuova azione"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm && (
            <form onSubmit={addManual} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
              data-testid="manual-action-form">
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Titolo azione (es. Adotta policy pari opportunità)" maxLength={200}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                data-testid="manual-action-title-input" aria-label="Titolo azione" />
              <div className="flex gap-3 flex-wrap">
                <Select value={pillar} onValueChange={setPillar}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="manual-action-pillar-select" aria-label="Pilastro">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Nessun pilastro</SelectItem>
                    <SelectItem value="E">E — Ambientale</SelectItem>
                    <SelectItem value="S">S — Sociale</SelectItem>
                    <SelectItem value="G">G — Governance</SelectItem>
                  </SelectContent>
                </Select>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrizione (opzionale)" maxLength={1000}
                  className="flex-1 min-w-[200px] h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                  data-testid="manual-action-description-input" aria-label="Descrizione azione" />
              </div>
              <Button type="submit" size="sm" disabled={saving || !title.trim()}
                className="bg-green-700 hover:bg-green-800 text-white"
                data-testid="manual-action-submit">
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}
                Aggiungi al piano
              </Button>
            </form>
          )}
          {plan && plan.manual_actions.length === 0 && !showForm && (
            <p className="text-sm text-slate-600" data-testid="manual-actions-empty">
              Nessuna azione manuale. Aggiungi obiettivi personalizzati del tuo piano ESG.
            </p>
          )}
          {plan && plan.manual_actions.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="manual-actions-list">
              {plan.manual_actions.map((m) => {
                const done = m.status === "DONE";
                return (
                  <li key={m.id} className={`py-3 flex flex-wrap items-center gap-3 ${done ? "opacity-60" : ""}`}
                    data-testid={`manual-action-row-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {m.pillar && <Badge className={PILLAR_CLS[m.pillar]}>{m.pillar}</Badge>}
                        <p className={`text-sm font-medium ${done ? "line-through text-slate-500" : "text-slate-900"}`}
                          data-testid={`manual-action-title-${m.id}`}>{m.title}</p>
                      </div>
                      {m.description && <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>}
                    </div>
                    {canWrite && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          disabled={busyKey === m.id} onClick={() => toggleManual(m)}
                          data-testid={`manual-action-toggle-${m.id}`}>
                          {done
                            ? <><RotateCcw className="w-3 h-3 mr-1" aria-hidden="true" />Riapri</>
                            : <><CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Completa</>}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          disabled={busyKey === m.id} onClick={() => deleteManual(m)}
                          data-testid={`manual-action-delete-${m.id}`}>
                          <Trash2 className="w-3 h-3" aria-hidden="true" />
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
